import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FirmwareUploadState,
  updateFirmwareUploadModalState,
  updateModalsCloseDrawerModal,
  updateShowFirmwareUploadModal,
} from "@src/reducers/your-reducer";
import { OtaDrawerDataInterface } from "@src/types/ota-update";
import { useI18N } from "@src/language/language-context";
import { validateFields } from "@src/cloud-connect/utils/validate-fields";
import {
  errorSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { RootState } from "@src/reducers/store";
import { postFirmwareUploadUseCase } from "@src/cloud-connect/ota-update/use-cases/post-upload-firmware";
import { isFilenameUsableUseCase } from "@src/cloud-connect/ota-update/use-cases/is-filename-usable";
import { OTA_FIRMWARE_VERSION_DRAWER_MODAL } from "@src/cloud-connect/ota-update/constants";

type UseOtaFirmwareVersionDrawerModalProps = {
  inputValues: OtaDrawerDataInterface;
  setInputValues: React.Dispatch<React.SetStateAction<OtaDrawerDataInterface>>;
};

export const useOtaFirmwareVersionDrawerModal = ({
  inputValues,
  setInputValues,
}: UseOtaFirmwareVersionDrawerModalProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();

  const connectionView = useSelector(
    (state: RootState) => state.yourSlice?.connectionView,
  );
  const authContext = useSelector(
    (state: RootState) => state.yourSlice?.authContext,
  );

  const texts = {
    firmwareVersionText: i18n("modules.otaDrawerForm.firmwareVersionText"),
    firmwareNameText: i18n("modules.otaDrawerForm.firmwareNameText"),
    releaseNewFirmwareText: i18n(
      "modules.otaFirmwareVersionDrawerModal.releaseNewFirmwareText",
    ),
    releaseFirmwareDetailsText: i18n(
      "modules.otaFirmwareVersionDrawerModal.releaseFirmwareDetailsText",
    ),
    releaseFirmwareText: i18n(
      "modules.otaFirmwareVersionDrawerModal.releaseFirmwareText",
    ),
    cancelText: i18n("modules.otaFirmwareVersionDrawerModal.cancelText"),
    fixValidateFieldText: i18n(
      "modules.otaFirmwareVersionDrawerModal.fixValidateFieldText",
    ),
    uploadFileText: i18n("modules.otaDrawerForm.uploadFileText"),
    uploadFailedText: i18n(
      "modules.otaFirmwareVersionDrawerModal.uploadFailedText",
    ),
  };

  const handleCloseDrawerModal = useCallback((): void => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: OTA_FIRMWARE_VERSION_DRAWER_MODAL,
      }),
    );
  }, [dispatch]);

  const handleOpenFirmwareUploadModal = useCallback((): void => {
    dispatch(updateShowFirmwareUploadModal(true));
    handleCloseDrawerModal();
  }, [dispatch, handleCloseDrawerModal]);

  const startFirmwareCreation = useCallback(async () => {
    try {
      const formData = new FormData();

      formData.append("source_id", connectionView.source.source_id);
      formData.append("uploader_id", authContext.userId);
      formData.append("version", inputValues.version);
      formData.append("size", String(inputValues?.file?.size));
      formData.append("filename", inputValues?.name);
      formData.append("release_notes_intro", inputValues.release_notes_intro);
      formData.append(
        "release_notes_improvements",
        inputValues.release_notes_improvements,
      );
      formData.append("release_notes_fixes", inputValues.release_notes_fixes);
      formData.append(
        "release_notes_general",
        inputValues.release_notes_general,
      );
      formData.append("type", "FIRMWARE");
      formData.append("file", inputValues?.file);

      const result = await postFirmwareUploadUseCase(formData);

      if (result.success) {
        dispatch(
          updateFirmwareUploadModalState(
            FirmwareUploadState.FIRMWARE_UPLOAD_COMPLETED,
          ),
        );
      } else {
        dispatch(
          updateFirmwareUploadModalState(
            FirmwareUploadState.FIRMWARE_UPLOAD_FAILED,
          ),
        );
      }
    } catch {
      errorSnackbar(texts.uploadFailedText, 5000);
    }
  }, [
    authContext.userId,
    connectionView.source.source_id,
    dispatch,
    inputValues?.file,
    inputValues?.name,
    inputValues.release_notes_fixes,
    inputValues.release_notes_general,
    inputValues.release_notes_improvements,
    inputValues.release_notes_intro,
    inputValues.version,
    texts.uploadFailedText,
  ]);

  const handleSubmit = useCallback(async () => {
    const fieldsToValidate = [
      {
        key: "name",
        label: texts.firmwareNameText,
      },
      {
        key: "version",
        label: texts.firmwareVersionText,
      },
    ];

    const emptyFields = validateFields(fieldsToValidate, inputValues);
    const missingFields = [...emptyFields];

    if (!inputValues.file) {
      missingFields.push(texts.uploadFileText);
    }

    if (missingFields.length > 0) {
      warningSnackbar(
        `${texts.fixValidateFieldText} ${missingFields.join(", ")}.`,
        5000,
      );
      return;
    }

    const response = await isFilenameUsableUseCase(
      inputValues?.file?.name,
      "FIRMWARE",
    );

    if (!response.success) {
      warningSnackbar(
        `Unable to use file "${inputValues?.file?.name}". Please check the file name.`,
        5000,
      );
      return;
    }

    handleOpenFirmwareUploadModal();
    startFirmwareCreation();
  }, [
    handleOpenFirmwareUploadModal,
    inputValues,
    startFirmwareCreation,
    texts.firmwareNameText,
    texts.fixValidateFieldText,
    texts.uploadFileText,
  ]);

  return {
    texts,
    handleCloseDrawerModal,
    handleSubmit,
    setInputValues,
  };
};
